import loadable from '@loadable/component';
import { useEffect } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useConfiguration } from '../../context/configurationContext';

import { camelize } from '../../util/string';
import { SectionRecentListings } from '../PageBuilder/SectionBuilder';
import FallbackPage from './FallbackPage';
import {
  ASSET_NAME,
  getImpactTrackerData,
  homeBoostedSectionId,
  howItWorksId,
  recentSectionId,
  searchHomeBoostedListings,
  searchRecentListings,
  shopByItemTypeId,
  whyChooseId,
} from './LandingPage.duck';

const PageBuilder = loadable(() =>
  import(/* webpackChunkName: "PageBuilder" */ '../PageBuilder/PageBuilder')
);

export const LandingPageComponent = props => {
  const {
    pageAssetsData,
    inProgress,
    error,
    onSearchHomeBoostedListings,
    onSearchRecentListings,
    homeBoostedListings,
    recentListings,
    onGetImpactTrackerData,
  } = props;
  const config = useConfiguration();
  const { homeBoostedSectionType, recentSectionType } = config.listing;

  useEffect(() => {
    onGetImpactTrackerData();
    onSearchHomeBoostedListings(config);
    onSearchRecentListings(config);
  }, [config, onSearchHomeBoostedListings, onSearchRecentListings]);

  // Construct custom page data
  const pageData = pageAssetsData?.[camelize(ASSET_NAME)]?.data;

  // Find the correct custom section based on section id
  const homeBoostedSectionIdx = pageData?.sections.findIndex(
    s => s.sectionId === homeBoostedSectionId
  );
  const homeBoostedSection = pageData?.sections[homeBoostedSectionIdx];

  const recentSectionIdx = pageData?.sections.findIndex(s => s.sectionId === recentSectionId);
  const recentSection = pageData?.sections[recentSectionIdx];
  const shopByItemTypeSectionIdx = pageData?.sections.findIndex(s =>
    s.sectionName.toLowerCase().includes('shop by item type')
  );
  const shopByItemTypeSection = pageData?.sections[shopByItemTypeSectionIdx];

  // Define the necessary props for the custom section
  const customHomeBoostedSection = {
    ...homeBoostedSection,
    sectionId: homeBoostedSectionId,
    sectionType: homeBoostedSectionType,
    listings: homeBoostedListings,
  };

  const customRecentSection = {
    ...recentSection,
    sectionId: recentSectionId,
    sectionType: recentSectionType,
    listings: recentListings,
  };

  const customShopByItemTypeSection = {
    ...shopByItemTypeSection,
    sectionId: shopByItemTypeId,
    sectionType: recentSectionType,
  };

  const howItWorksSectionIdx = pageData?.sections.findIndex(
    s => s.sectionId === howItWorksId || s.sectionName.toLowerCase().includes('how it works')
  );
  const howItWorksSection = pageData?.sections[howItWorksSectionIdx];

  const whyChooseSectionIdx = pageData?.sections.findIndex(
    s => s.sectionId === whyChooseId || s.sectionName.toLowerCase().includes('why choose')
  );

  const whyChooseSection = pageData?.sections[whyChooseSectionIdx];

  const customHowItWorksSection = {
    ...howItWorksSection,
    sectionId: howItWorksId,
    sectionType: recentSectionType,
    listings: [],
  };

  const customWhyChooseSection = {
    ...whyChooseSection,
    sectionId: whyChooseId,
    sectionType: recentSectionType,
    listings: [],
  };

  // Replace the original section with the custom section object
  // in custom page data
  const customSections =
    pageData?.sections?.map((s, idx) =>
      idx === 0
        ? { ...s, descriptionAlign: 'center' }
        : idx === homeBoostedSectionIdx
        ? customHomeBoostedSection
        : idx === recentSectionIdx
        ? customRecentSection
        : idx === howItWorksSectionIdx
        ? customHowItWorksSection
        : idx === whyChooseSectionIdx
        ? customWhyChooseSection
        : idx === shopByItemTypeSectionIdx
        ? customShopByItemTypeSection
        : s
    ) || null;

  const customPageData = pageData
    ? {
        ...pageData,
        sections: customSections,
      }
    : pageData;

  return (
    <PageBuilder
      pageAssetsData={customPageData}
      options={{
        sectionComponents: {
          [homeBoostedSectionType]: { component: SectionRecentListings },
          [recentSectionType]: { component: SectionRecentListings },
        },
      }}
      inProgress={inProgress}
      error={error}
      fallbackPage={<FallbackPage error={error} />}
    />
  );
};

const mapStateToProps = state => {
  const { pageAssetsData, inProgress, error } = state.hostedAssets || {};
  const { homeBoostedListings, recentListings } = state.LandingPage;

  return {
    pageAssetsData,
    inProgress,
    error,
    homeBoostedListings,
    recentListings,
  };
};

const mapDispatchToProps = dispatch => ({
  onSearchHomeBoostedListings: config => dispatch(searchHomeBoostedListings(config)),
  onSearchRecentListings: config => dispatch(searchRecentListings(config)),
  onGetImpactTrackerData: () => dispatch(getImpactTrackerData()),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const LandingPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(LandingPageComponent);

export default LandingPage;
